import {
  AnalysisTypes,
  Ansatt,
  Avdeling,
  CompactAnalysisType,
  CompactPlan,
  ContractAnalysisType,
  Contracts,
  EfsaSampnts,
  ImportantMessage,
  HistoryTimeline,
  Labs,
  Matrices,
  NewsArticles,
  OkProgram,
  OrdningsVerdier,
  PayloadSchemas,
  Plans,
  PlanLegislation,
  Products,
  Produsent,
  Purpose,
  Region,
  Requisitions,
  Result,
  Samples,
  SampleTypes,
  SinglePlan,
  Seksjon,
  StatusDurationReport,
  SubPurpose,
  Substances,
  Tasks,
  TaskDelegationAudit,
  TaskDelegation,
  TilsynsobjektV2,
  User,
  Workspace
} from '../types/types'

type PlanInstructions = Partial<
  Pick<
    SinglePlan,
    | 'samplingInstructions'
    | 'storageInstructions'
    | 'labelingInstructions'
    | 'shippingInstructions'
    | 'followUpInstructions'
  >
> &
  Partial<
    Pick<
      SinglePlan,
      | 'directDue'
      | 'directContractRef'
      | 'directWorkspaceId'
      | 'directPurposeId'
    >
  >

export type MockCompactPlan = CompactPlan &
  Partial<PlanInstructions> & {
    contractRef?: string | null
  }

interface IMockData {
  planLegislation: PlanLegislation[]
  plans: Omit<Plans, 'plans'> & { plans: MockCompactPlan[] }
  tasks: Tasks
  samples: Samples
  analysisTypes: AnalysisTypes
  sampleTypes: SampleTypes
  requisitions: Requisitions
  results: Result[]
  labs: Labs
  matrices: Matrices
  products: Products
  substances: Substances
  tilsynsobjekter: TilsynsobjektV2[]
  sampntTypes: EfsaSampnts
  produsenter: Produsent[]
  payloadSchemas: PayloadSchemas
  regioner: Region[]
  avdelinger: Avdeling[]
  seksjoner: Seksjon[]
  news: NewsArticles
  ordningsverdier: OrdningsVerdier
  users: User[]
  okPrograms: OkProgram[]
  contracts: Contracts
  importantMessages: ImportantMessage[]
  purposes: Purpose[]
  subPurposes: SubPurpose[]
  teams: { id: number; name: string; seksjonId?: string; avdelingId?: string }[]
  ansatte: Array<Ansatt & { id: string }>
  taskDelegationAudits: TaskDelegationAudit[]
  taskDelegations: Record<number, TaskDelegation>
  sampleHistories: Record<string, HistoryTimeline>
  statusDurationReports: StatusDurationReport[]
  workspaces: Workspace[]
}

const initialMockId = 1_000_000
let nextMockId = initialMockId

export const createMockId = (): number => nextMockId++

const resetMockId = (): void => {
  nextMockId = initialMockId
}

const payloadSchema = {
  type: 'object',
  properties: {
    entityId: {
      'x-include-in-requisition': true,
      $ref: '#/definitions/entityId'
    },
    gender: {
      'x-include-in-requisition': true,
      $ref: '#/definitions/gender'
    },
    conditionAtSampling: {
      'x-include-in-requisition': true,
      $ref: '#/definitions/conditionAtSampling'
    },
    age: {
      'x-include-in-requisition': true,
      $ref: '#/definitions/age'
    },
    treatmentLastFourWeeks: {
      'x-include-in-requisition': true,
      $ref: '#/definitions/treatmentLastFourWeeks'
    },
    organicProduction: {
      'x-include-in-requisition': true,
      $ref: '#/definitions/organicProduction'
    }
  },
  required: [
    'entityId',
    'gender',
    'conditionAtSampling',
    'age',
    'treatmentLastFourWeeks',
    'organicProduction'
  ],
  allOf: [
    {
      $ref: '#/conditionals/gender'
    },
    {
      $ref: '#/conditionals/treatmentLastFourWeeks'
    }
  ]
}

const getDefaultMockData = (): IMockData => {
  const regioner: Region[] = [
    {
      id: 'r1',
      kortnavn: 'R1',
      navn: 'Region 1'
    },
    {
      id: 'r2',
      kortnavn: 'R2',
      navn: 'Region 2'
    }
  ]
  const avdelinger: Avdeling[] = [
    {
      id: 'a1',
      kortnavn: 'A1',
      navn: 'Avdeling 1',
      regionId: 'r1'
    },
    {
      id: 'a2',
      kortnavn: 'A2',
      navn: 'Avdeling 2',
      regionId: 'r1'
    },
    {
      id: 'a3',
      kortnavn: 'A3',
      navn: 'Avdeling 3',
      regionId: 'r2'
    },
    {
      id: 'a4',
      kortnavn: 'A4',
      navn: 'Avdeling 4',
      regionId: 'r2'
    },
    {
      id: 'a5',
      kortnavn: 'A5',
      navn: 'Avdeling uten underenheter',
      regionId: 'r1'
    },
    {
      id: 'a6',
      kortnavn: 'A6',
      navn: 'Avdeling med team direkte',
      regionId: 'r1'
    }
  ]

  const seksjoner = [
    {
      id: 's1',
      kortnavn: 'S1',
      navn: 'Seksjon 1',
      avdelingId: 'a1'
    },
    {
      id: 's2',
      kortnavn: 'S2',
      navn: 'Seksjon 2',
      avdelingId: 'a2'
    },
    {
      id: 's3',
      kortnavn: 'S3',
      navn: 'Seksjon 3',
      avdelingId: 'a3'
    },
    {
      id: 's4',
      kortnavn: 'S4',
      navn: 'Seksjon 4',
      avdelingId: 'a4'
    }
  ]

  const payloadSchemas: PayloadSchemas = [
    {
      id: 1,
      name: 'Schema 1',
      schema: JSON.stringify(payloadSchema),
      userId: 1,
      lastUpdated: '2023-01-01T00:00:00Z',
      labId: 1,
      etag: 'etag'
    }
  ]
  const labs: Labs = [
    {
      id: 1,
      name: 'Lab 1'
    },
    {
      id: 2,
      name: 'Lab 2'
    }
  ]
  const tilsynsobjekter: TilsynsobjektV2[] = [
    {
      idstring: '1',
      navn: 'Tilsynsobjekt 1',
      virksomhetsnavn: 'Virksomhet 1',
      identiteter: [
        {
          identitetstype: { id: 'IDENTITETSTYPE$PRODUSENTNUMMER' },
          gyldigFra: '2020-01-01T00:00:00Z',
          verdi: '1111111111'
        }
      ]
    },
    {
      idstring: '2',
      navn: 'Tilsynsobjekt 2',
      virksomhetsnavn: 'Virksomhet 2',
      identiteter: [
        {
          identitetstype: { id: 'IDENTITETSTYPE$PRODUSENTNUMMER' },
          gyldigFra: '2020-01-01T00:00:00Z',
          verdi: '2222222222'
        }
      ]
    }
  ]
  const produsenter: Produsent[] = [
    {
      tilsynsobjektId: '1',
      virksomhetsNavn: 'Virksomhet 1',
      aktivitet: 'Aktivitet 1',
      produsentnummer: '1111111111'
    },
    {
      tilsynsobjektId: '2',
      virksomhetsNavn: 'Virksomhet 2',
      aktivitet: 'Aktivitet 2',
      produsentnummer: '2222222222'
    }
  ]
  const sampntTypes: EfsaSampnts = [
    {
      id: 1,
      code: 'EFSA1',
      nameNo: 'Gård',
      nameEn: 'Farm'
    },
    {
      id: 2,
      code: 'E311A',
      nameNo: 'Slakteri',
      nameEn: 'Slaughterhouse'
    }
  ]
  const substances: Substances = [
    {
      id: 1,
      name: 'Substance 1',
      code: 'Substance 1 Code'
    },
    {
      id: 2,
      name: 'Substance 2',
      code: 'Substance 2 Code'
    }
  ]
  const matrices: Matrices = [
    {
      id: 1,
      name: 'Matrix 1',
      code: 'Matrix 1 Code'
    },
    {
      id: 2,
      name: 'Matrix 2',
      code: 'Matrix 2 Code'
    }
  ]
  const okPrograms: OkProgram[] = [
    {
      id: 1,
      number: '100001',
      name: 'OK-program villfisk',
      etag: 'etag-ok-1'
    },
    {
      id: 2,
      number: '100002',
      name: 'OK-program oppdrettsfisk',
      etag: 'etag-ok-2'
    }
  ]
  const purposes: Purpose[] = [
    {
      id: 1,
      name: 'Hensikt 1',
      etag: 'etag-purpose-1'
    },
    {
      id: 2,
      name: 'Hensikt 2',
      etag: 'etag-purpose-2'
    }
  ]
  const subPurposes: SubPurpose[] = [
    {
      id: 1,
      name: 'Underhensikt 1',
      etag: 'etag-sub-purpose-1'
    },
    {
      id: 2,
      name: 'Underhensikt 2',
      etag: 'etag-sub-purpose-2'
    }
  ]
  const products: Products = [
    {
      id: 1,
      name: 'Product 1',
      code: 'Product 1 Code'
    },
    {
      id: 2,
      name: 'Product 2',
      code: 'Product 2 Code'
    }
  ]
  const sampleTypes: SampleTypes = [
    {
      id: 1,
      matrixId: matrices[0].id,
      matrix: matrices[0].name,
      matrixCode: matrices[0].code,
      productId: products[0].id,
      product: products[0].name,
      productCode: products[0].code,
      description: ''
    },
    {
      id: 2,
      matrixId: matrices[1].id,
      matrix: matrices[1].name,
      matrixCode: matrices[1].code,
      productId: products[1].id,
      product: products[1].name,
      productCode: products[1].code,
      description: ''
    }
  ]

  const analysisTypes: AnalysisTypes = [
    {
      id: 1,
      sampleType: sampleTypes[0],
      substances: [
        {
          id: 1,
          analysisTypeId: 1,
          substance: substances[0]
        },
        {
          id: 2,
          analysisTypeId: 1,
          substance: substances[1]
        }
      ],
      payloadSchema: payloadSchemas[0]
    },
    {
      id: 2,
      sampleType: sampleTypes[1],
      substances: []
    }
  ]

  const contractAnalysisTypes: ContractAnalysisType[] = [
    {
      id: 1,
      analysisType: analysisTypes[0],
      price: 1000,
      mtApproval: true,
      labApproval: true,
      updatedTime: '2024-01-01T00:00:00Z',
      updatedBy: 'Testbruker 1'
    },
    {
      id: 2,
      analysisType: analysisTypes[1],
      price: 1500,
      mtApproval: false,
      labApproval: true,
      approvalComment: 'Venter på godkjenning fra MT',
      updatedTime: '2024-01-02T00:00:00Z',
      updatedBy: 'Testbruker 1'
    }
  ]

  const contracts: Contracts = [
    {
      id: 1,
      title: 'Testkontrakt 1',
      etag: 'etag-contract-1',
      labId: labs[0].id,
      analysisTypes: contractAnalysisTypes,
      ownerId: 'avdeling:a1',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2026-12-31T00:00:00Z'
    },
    {
      id: 2,
      title: 'Testkontrakt 2',
      etag: 'etag-contract-2',
      labId: labs[1].id,
      analysisTypes: [],
      ownerId: 'seksjon:s4',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2026-12-31T00:00:00Z'
    }
  ]

  const compactAnalysisTypes: CompactAnalysisType[] = [
    {
      id: 1,
      sampleTypeId: 1,
      substances: [
        {
          id: 1,
          analysisTypeId: 1,
          substanceId: 1
        },
        {
          id: 2,
          analysisTypeId: 1,
          substanceId: 2
        }
      ]
    },
    {
      id: 2,
      sampleTypeId: 2,
      substances: []
    }
  ]

  const ordningsverdier = [
    {
      id: 1,
      kategoriNavn: 'Kategori 1',
      ordningsverdi: 'Ordning 1'
    }
  ]

  const tasks: Tasks = [
    {
      id: 1,
      planIds: [1],
      ownerId: 'avdeling:a1',
      title: 'Urinprøve storfe',
      analysisType: analysisTypes[0],
      quantity: 2,
      matrixAmount: { value: 300, unit: 'GRAM' },
      lab: labs[0],
      sampntType: sampntTypes[0],
      ordningsVerdi: ordningsverdier[0],
      planStatus: 'active',
      analysisTypeApprovalStatus: 'approved',
      etag: 'etag'
    },
    {
      id: 2,
      planIds: [1],
      ownerId: 'seksjon:s4',
      title: 'Leverprøve storfe',
      analysisType: analysisTypes[1],
      quantity: 2,
      matrixAmount: { value: 1.5, unit: 'KILOGRAM' },
      lab: labs[1],
      sampntType: sampntTypes[0],
      ordningsVerdi: ordningsverdier[0],
      planStatus: 'active',
      analysisTypeApprovalStatus: 'approved',
      etag: 'etag'
    }
  ]

  const taskDelegationAudits: TaskDelegationAudit[] = [
    {
      id: 1,
      operation: 'CREATE',
      timestamp: '2024-01-10T12:00:00Z',
      changedByUserId: '1234567890',
      actorName: 'Kari Saksbehandler',
      taskDetailId: 1,
      parentId: 0,
      quantity: 2,
      ownerId: 'avdeling:a1',
      etag: 'etag-audit-1'
    }
  ]

  const taskDelegations = Object.fromEntries(
    tasks.map((task) => [
      task.id,
      {
        taskId: task.id,
        taskDetailId: task.id,
        plannedQuantity: task.quantity ?? 0,
        entries:
          task.id === 2
            ? [
                {
                  taskDelegationId: 101,
                  path: ['region:r1'],
                  ownerId: 'region:r1',
                  plannedQuantity: 2,
                  delegatedQuantity: 0,
                  retainedQuantity: 2,
                  samplesTaken: 0,
                  isLeaf: true
                }
              ]
            : []
      }
    ])
  )

  const workspaces: Workspace[] = [
    {
      id: 1,
      name: 'Landdyr og slakteri',
      initials: 'LS',
      visualKey: 'land-animals',
      sortOrder: 10,
      active: true,
      ownerId: 'region:r1',
      description:
        'Tilsyn med helse og velferd hos storfe, svin og fjørfe gjennom hele produksjonskjeden - fra gård til slakteri. Overvåking av sykdommer og legemiddelrester sikrer trygg mat fra landdyr.'
    },
    {
      id: 2,
      name: 'Mat og grensekontroll',
      initials: 'MG',
      visualKey: 'border-control',
      sortOrder: 20,
      active: true,
      description:
        'Kontroll av næringsmidler i omsetning og ved grensepassering. Overvåking av kjemiske og biologiske farer sikrer at mat som omsettes i Norge er trygg for forbrukerne.'
    },
    {
      id: 3,
      name: 'Planter, fôr og drikkevann',
      initials: 'PD',
      visualKey: 'plants-feed-water',
      sortOrder: 30,
      active: true,
      description:
        'Helseovervåking av planter, fôrvarer og drikkevann. Tilsyn med plantevernmiddelrester og plantehelse sikrer trygge innsatsvarer og godt drikkevann i hele produksjonskjeden.'
    },
    {
      id: 4,
      name: 'Akvakultur',
      initials: 'AK',
      visualKey: 'aquaculture',
      sortOrder: 40,
      active: true,
      ownerId: 'region:r2',
      description:
        'Overvåking av sykdommer og parasitter i norsk fiskeoppdrett. Kartlegging av ILA-virus, lakselus og andre helsetrusler bidrar til bærekraftig og frisk fiskeproduksjon.'
    }
  ]

  for (const workspace of workspaces) {
    workspace.kind = {
      code: 'TILSYNSDIVISJON',
      title: 'Tilsynsdivisjoner',
      sortOrder: 20
    }
  }
  for (const [index, name] of [
    'Kjemisk mattrygghet',
    'Biologisk mattrygghet',
    'Fiskehelse',
    'Dyrehelse',
    'Planter og innsatsvarer'
  ].entries()) {
    workspaces.push({
      id: 10 + index,
      name,
      initials: ['KM', 'BM', 'FH', 'DH', 'PI'][index],
      visualKey: [
        'plants-feed-water',
        'border-control',
        'aquaculture',
        'land-animals',
        'plants-feed-water'
      ][index],
      sortOrder: index,
      active: true,
      kind: { code: 'FAGSEKSJON', title: 'Fagseksjoner', sortOrder: 10 }
    })
  }

  const compactPlans: MockCompactPlan[] = [
    {
      id: 1,
      ownerId: 'self',
      due: '2023-05-17T00:00:00Z',
      directDue: '2023-05-17T00:00:00Z',
      tasks: [
        {
          id: 1,
          planIds: [1],
          title: 'Urinprøve storfe',
          analysisTypeId: analysisTypes[0].id,
          labId: labs[0].id,
          sampntTypeId: sampntTypes[0].id,
          delegations: [],
          planStatus: 'active',
          countSubSamples: false,
          ordningsVerdi: ordningsverdier[0],
          locationRequired: false
        },
        {
          id: 2,
          planIds: [1],
          title: 'Leverprøve storfe',
          analysisTypeId: analysisTypes[1].id,
          labId: labs[1].id,
          sampntTypeId: sampntTypes[0].id,
          delegations: [],
          planStatus: 'active',
          countSubSamples: false,
          ordningsVerdi: ordningsverdier[0],
          locationRequired: false
        }
      ],
      description: 'Test plan 1',
      parents: [],
      children: [],
      status: 'active',
      directCount: 0,
      propagatedCount: 0,
      totalCount: 4,
      totalQuantity: 4,
      directQuantity: 0,
      propagatedQuantity: 0,
      etag: 'etag-plan-1',
      samplingInstructions: null,
      storageInstructions: null,
      labelingInstructions: null,
      shippingInstructions: null,
      followUpInstructions: null,
      contractId: contracts[0].id,
      okProgramId: okPrograms[0].id,
      workspaceId: 1,
      directWorkspaceId: 1,
      directContractRef: null,
      directPurposeId: null
    },
    {
      id: 2,
      ownerId: 'self',
      due: '2023-09-01T00:00:00Z',
      directDue: '2023-09-01T00:00:00Z',
      tasks: [],
      description: 'Test plan 2',
      parents: [],
      children: [],
      status: 'active',
      directCount: 0,
      propagatedCount: 0,
      totalCount: 0,
      totalQuantity: 4,
      directQuantity: 0,
      propagatedQuantity: 0,
      etag: 'etag-plan-2',
      contractId: contracts[1].id,
      okProgramId: okPrograms[1].id,
      workspaceId: 2,
      directWorkspaceId: 2,
      directContractRef: null,
      directPurposeId: null
    },
    {
      id: 4,
      ownerId: 'self',
      due: '2024-03-01T00:00:00Z',
      directDue: '2024-03-01T00:00:00Z',
      tasks: [],
      description: 'Plan uten tilsynsdivisjon',
      parents: [],
      children: [],
      status: 'active',
      directCount: 0,
      propagatedCount: 0,
      totalCount: 0,
      totalQuantity: 0,
      directQuantity: 0,
      propagatedQuantity: 0,
      etag: 'etag-plan-4',
      directWorkspaceId: null,
      directContractRef: null,
      directPurposeId: null
    }
  ]

  const teams = [
    { id: 1, seksjonId: 's1', name: 'Team 1' },
    { id: 2, seksjonId: 's4', name: 'Team 2' },
    { id: 3, avdelingId: 'a1', name: 'Team under avdeling' },
    { id: 4, avdelingId: 'a6', name: 'Team direkte under avdeling 6' }
  ]

  const ansatte: Array<Ansatt & { id: string }> = [
    {
      id: '1234567890',
      navn: 'Testbruker 1',
      regionId: 'r1',
      avdelingId: 'a1',
      seksjonId: 's1'
    }
  ]

  const importantMessages: ImportantMessage[] = [
    {
      id: 1,
      title: 'Viktig melding',
      text: 'Dette er en testmelding.',
      startDate: '2024-01-01T00:00:00Z',
      endDate: '2026-12-31T00:00:00Z',
      createdBy: '1234567890',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
      etag: 'etag-message-1'
    }
  ]

  const statusDurationReports: StatusDurationReport[] = [
    {
      externalId: 'a1',
      type: 'avdeling',
      statuses: [
        { status: 'SampleTaken', numSamples: 42, avgDays: 1.2 },
        { status: 'SampleSent', numSamples: 42, avgDays: 2.8 },
        { status: 'SampleReceived', numSamples: 40, avgDays: 1.5 },
        { status: 'AnalysisStarted', numSamples: 39, avgDays: 3.6 },
        { status: 'ReadyToProcess', numSamples: 38, avgDays: 2.1 },
        { status: 'Completed', numSamples: 38, avgDays: 5.4 },
        { status: 'Compromised', numSamples: 3, avgDays: 4.2 }
      ]
    },
    {
      externalId: 'a2',
      type: 'avdeling',
      statuses: [
        { status: 'SampleTaken', numSamples: 28, avgDays: 2.0 },
        { status: 'SampleSent', numSamples: 28, avgDays: 5.5 },
        { status: 'SampleReceived', numSamples: 26, avgDays: 2.3 },
        { status: 'AnalysisStarted', numSamples: 25, avgDays: 6.8 },
        { status: 'ReadyToProcess', numSamples: 24, avgDays: 3.4 },
        { status: 'Completed', numSamples: 24, avgDays: 9.1 }
      ]
    },
    {
      externalId: 'a3',
      type: 'avdeling',
      statuses: [
        { status: 'SampleTaken', numSamples: 15, avgDays: 0.8 },
        { status: 'SampleSent', numSamples: 15, avgDays: 1.4 },
        { status: 'SampleReceived', numSamples: 15, avgDays: 0.9 },
        { status: 'AnalysisStarted', numSamples: 14, avgDays: 2.2 },
        { status: 'ReadyToProcess', numSamples: 14, avgDays: 1.0 },
        { status: 'Completed', numSamples: 14, avgDays: 3.3 },
        { status: 'Compromised', numSamples: 1, avgDays: 2.0 }
      ]
    },
    {
      externalId: 'a4',
      type: 'avdeling',
      statuses: [
        { status: 'SampleTaken', numSamples: 63, avgDays: 1.6 },
        { status: 'SampleSent', numSamples: 63, avgDays: 3.9 },
        { status: 'SampleReceived', numSamples: 61, avgDays: 2.0 },
        { status: 'AnalysisStarted', numSamples: 60, avgDays: 4.7 },
        { status: 'ReadyToProcess', numSamples: 58, avgDays: 2.9 },
        { status: 'Completed', numSamples: 58, avgDays: 7.2 },
        { status: 'Compromised', numSamples: 5, avgDays: 6.5 }
      ]
    }
  ]

  const sampleHistories: Record<string, HistoryTimeline> = {
    '1': {
      items: [
        {
          id: 'audit:sample:1:2024-01-01T10:00:00Z',
          entityType: 'sample',
          entityId: '1',
          source: 'audit',
          kind: 'update',
          occurredAt: '2024-01-01T10:00:00Z',
          actor: {
            id: 'user:123',
            type: 'user'
          },
          title: 'Prøven er endret',
          changes: [
            {
              field: 'comment',
              label: 'Kommentar',
              from: 'Gammel kommentar',
              to: 'Ny kommentar'
            }
          ]
        },
        {
          id: 'event:1',
          entityType: 'sample',
          entityId: '1',
          source: 'event',
          kind: 'SampleSent',
          occurredAt: '2024-01-02T10:00:00Z',
          title: 'Prøve sendt'
        }
      ]
    }
  }

  const plans: Plans = {
    plans: compactPlans,
    analysisTypes: compactAnalysisTypes,
    substances,
    labs,
    sampntTypes,
    payloadSchemas,
    users: []
  }

  const results: Result[] = [
    {
      subSampleId: 1,
      resultSets: [
        {
          resultId: 1,
          resultLines: [],
          status: 'NEGATIVE'
        }
      ]
    },
    {
      subSampleId: 2,
      resultSets: [
        {
          resultId: 2,
          resultLines: [],
          status: 'POSITIVE'
        }
      ]
    },

    {
      subSampleId: 3,
      resultSets: [
        {
          resultId: 3,
          resultLines: [],
          status: 'NEGATIVE'
        }
      ]
    },
    {
      subSampleId: 4,
      resultSets: [
        {
          resultId: 4,
          resultLines: [],
          status: 'POSITIVE'
        }
      ]
    }
  ]

  const requisitions: Requisitions = [
    {
      id: 1,
      analysisType: analysisTypes[0],
      lab: labs[0],
      createdDate: '2023-01-02T00:00:00Z',
      externalCreatedDate: '2023-01-02T00:00:00Z',
      results: {
        status: 'NEGATIVE',
        complete: true,
        subSamples: {
          1: results[0]
        }
      },
      etag: 'etag',
      resultsClosedDate: '2023-01-02T00:00:00Z'
    },
    {
      id: 2,
      analysisType: analysisTypes[0],
      lab: labs[0],
      createdDate: '2023-01-02T00:00:00Z',
      externalCreatedDate: '2023-01-02T00:00:00Z',
      results: {
        status: 'POSITIVE',
        complete: true,
        subSamples: {
          2: results[1]
        }
      },
      etag: 'etag',
      resultsClosedDate: '2023-01-02T00:00:00Z'
    },
    {
      id: 3,
      analysisType: analysisTypes[0],
      lab: labs[0],
      createdDate: '2023-01-02T00:00:00Z',
      results: {
        status: 'NEGATIVE',
        complete: false,
        subSamples: {
          3: results[2]
        }
      },
      etag: 'etag'
    },
    {
      id: 4,
      analysisType: analysisTypes[0],
      lab: labs[0],
      createdDate: '2023-01-02T00:00:00Z',
      results: {
        status: 'POSITIVE',
        complete: false,
        subSamples: {
          4: results[3]
        }
      },
      etag: 'etag'
    },
    {
      id: 5,
      analysisType: analysisTypes[0],
      lab: labs[0],
      createdDate: '2023-01-02T00:00:00Z',
      externalCreatedDate: '2023-01-02T00:00:00Z',
      etag: 'etag'
    },
    {
      id: 6,
      analysisType: analysisTypes[0],
      lab: labs[0],
      createdDate: '2023-01-02T00:00:00Z',
      etag: 'etag'
    }
  ]
  const samples: Samples = [
    {
      id: '1',
      taskId: 1,
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Domkirkeodden',
      etag: 'etag',
      geometry: {
        type: 'Point',
        coordinates: [11.03529, 60.79218]
      },
      requisitions: [requisitions[0]],
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '2',
      taskId: 1,
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Tårnvegen 41',
      etag: 'etag',
      geometry: {
        type: 'Point',
        coordinates: [10.92908, 60.87751]
      },
      requisitions: [requisitions[1]],
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '3',
      taskId: 2,
      sampleType: sampleTypes[1],
      oversightObject: tilsynsobjekter[1].idstring,
      externalReferenceId: produsenter[1].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-04-03T00:00:00Z',
      comment: 'Hafjell',
      etag: 'etag',
      geometry: {
        type: 'Point',
        coordinates: [10.52504, 61.22969]
      },
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '4',
      taskId: 2,
      sampleType: sampleTypes[1],
      oversightObject: tilsynsobjekter[1].idstring,
      externalReferenceId: produsenter[1].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Stensberggata',
      etag: 'etag',
      geometry: {
        type: 'Point',
        coordinates: [10.73576, 59.9214]
      },
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '5',
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Test sample (SampleCreated)',
      etag: 'etag',
      requisitions: [requisitions[5]],
      state: 'SampleCreated',
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '6',
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Test sample (SampleReceived)',
      etag: 'etag',
      requisitions: [requisitions[5]],
      state: 'SampleReceived',
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '7',
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Test sample (AnalysisStarted)',
      etag: 'etag',
      requisitions: [requisitions[5]],
      state: 'AnalysisStarted',
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '8',
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Test sample (ReadyToProcess)',
      etag: 'etag',
      requisitions: [requisitions[5]],
      state: 'ReadyToProcess',
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '9',
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Test sample (Completed)',
      etag: 'etag',
      requisitions: [requisitions[5]],
      state: 'Completed',
      nameRedacted: false,
      accuratePosition: false
    },
    {
      id: '10',
      sampleType: sampleTypes[0],
      oversightObject: tilsynsobjekter[0].idstring,
      externalReferenceId: produsenter[0].produsentnummer,
      sampntType: sampntTypes[0],
      takenDate: '2023-01-02T00:00:00Z',
      comment: 'Test sample (Compromised)',
      etag: 'etag',
      requisitions: [requisitions[5]],
      state: 'Compromised',
      subSamples: [
        {
          id: 1,
          subSampleId: 1,
          compromised: true,
          compromised_reason: 'Kansellert pga. feil i prøvetaking'
        }
      ],
      nameRedacted: false,
      accuratePosition: false
    }
  ]
  const news: NewsArticles = [
    {
      id: 1,
      subject: 'Test',
      date: '2023-01-01T15:00:00Z',
      app: 'ProveTa',
      endDate: '2025-01-05T15:00:00Z',
      icon: 'bug'
    },
    {
      id: 2,
      subject: 'Sok er na lagt til',
      date: '2023-01-01T15:00:00Z',
      app: 'ProveTa',
      endDate: '2024-01-02T15:00:00Z',
      icon: 'merge'
    },
    {
      id: 3,
      subject: 'Nyheter i Prøveplan',
      date: '2024-01-01T15:00:00Z',
      app: 'ProvePlan',
      endDate: '2026-01-02T15:00:00Z',
      icon: 'merge'
    }
  ]
  const users: User[] = [
    {
      id: '1234567890',
      name: 'Testbruker 1',
      email: 'testbruker1@example.test',
      employeeId: '1001',
      type: 'employee',
      settings:
        '{"avdeling":{"selectedRegionId":"r1","selectedAvdelingId":"a1"},"theme":"system","filters":{"planIds":[],"productIds":[],"matrixIds":[],"sampntTypeIds":[],"substanceIds":[],"status":[]},"filtersTasks":{"productIds":[],"sampntTypeIds":[],"substanceIds":[],"planIds":[],"matrixIds":[],"status":[],"isCompleted":false},"filterTakeSamples":{"productIds":[],"sampntTypeIds":[],"substanceIds":[],"planIds":[],"matrixIds":[],"status":[]},"filterPlanSamples":{"productIds":[],"sampntTypeIds":[],"substanceIds":[],"planIds":[],"matrixIds":[],"status":[]},"filterRequisitions":{"productIds":[],"sampntTypeIds":[],"substanceIds":[],"planIds":[],"matrixIds":[],"status":[]}}'
    },
    {
      id: 'permission-overview-user',
      name: 'Bjarne Kornelius Sveivebråten Oppistuggu',
      email: 'testbruker2@example.test',
      employeeId: '1002',
      type: 'employee',
      permissions: [
        { id: 100, responsibility: 'PROGRAM_MANAGER' },
        { id: 101, responsibility: 'COORDINATOR', externalId: 'region:r2' }
      ]
    },
    {
      id: 'test-user-3',
      name: 'Gunda Bertine Krumkakebakken Nørstpå',
      email: 'testbruker3@example.test',
      employeeId: '1003',
      type: 'employee',
      permissions: [
        { id: 102, responsibility: 'COORDINATOR', externalId: 'region:r1' },
        { id: 103, responsibility: 'PROGRAM_MANAGER' }
      ]
    },
    {
      id: 'test-user-4',
      name: 'Ludvig Anselm Skranglemoen Øvre Baklia',
      email: 'testbruker4@example.test',
      employeeId: '1004',
      type: 'employee',
      permissions: [
        { id: 104, responsibility: 'ADMIN' },
        { id: 105, responsibility: 'COORDINATOR', externalId: 'team:1' }
      ]
    },
    {
      id: 'test-user-5',
      name: 'Testbruker 5',
      email: 'testbruker5@example.test',
      employeeId: '1005',
      type: 'employee'
    },
    {
      id: 'test-user-6',
      name: 'Testbruker 6',
      email: 'testbruker6@example.test',
      employeeId: '1006',
      type: 'employee'
    }
  ]
  return {
    planLegislation: [],
    plans,
    tasks,
    samples,
    analysisTypes,
    sampleTypes,
    labs,
    requisitions,
    results,
    matrices,
    products,
    substances,
    tilsynsobjekter,
    sampntTypes,
    produsenter,
    payloadSchemas,
    regioner,
    avdelinger,
    seksjoner,
    news,
    ordningsverdier,
    users,
    okPrograms,
    contracts,
    importantMessages,
    purposes,
    subPurposes,
    teams,
    ansatte,
    taskDelegationAudits,
    taskDelegations,
    sampleHistories,
    statusDurationReports,
    workspaces
  }
}

export const resetMockData = (): void => {
  resetMockId()
  Object.assign(mockData, getDefaultMockData())
}

export const mockData = getDefaultMockData()
